import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { translate, allDirectionsLabel } from "../../types/EnumTranslations";
import { getMaterialTranslation, useMaterials } from "../../contexts/MaterialsContext";
import InfoPanel, { InfoItem } from "../../Components/InfoPanel";
import { BaseComponentImpl } from "../../types/implementations";
import { Building, RoofAnchorPoint, RoofAccessPoint, BlueprintLocation, PhotoModel, PhotoLocation, ColorModel } from "../../types/protocols";
import { BaseType, HazardType, BuildComponentTypeDefinition, CardinalDirection, RoofShape } from "../../types/Enums";
import { getCategoryField } from "../../Helpers/componentTypeCategories";
import { getLifetimeForDirection } from '../Förvaltning/TemplatesEditor/types';
import { calculateCurrentCondition, MS_PER_YEAR } from '../../Helpers/conditionUtils';
import BlueprintWithMarkers from "../../Components/BlueprintWithMarkers";
import PhotoGallery from "../../Components/PhotoGallery";
import ChildComponentsSection from "./ChildComponentsSection";
import ParentComponentCard from "../../Components/ParentComponentCard";
import PhotoWithMarker from "../../Components/PhotoWithMarker";
import PhotoThumbnailWithMarker from "../../Components/PhotoThumbnailWithMarker";
import { PHOTO_LOCATION_SIDE_LABELS, photoLocationLabel, photoLocationList } from "../../Helpers/photoLocations";
import { Link } from "react-router-dom";
import { useOpenPath } from "../../hooks/useOpenPath";
import { useBuildComponentTypes } from "../../contexts/BuildComponentTypesContext";
import { getAllBuildComponentTypes } from "../../Helpers/BuildComponentTypesStore";
import { RiskBadge } from "../BuildingDetails/RisksOverview/RisksOverviewList";
import RoofSafetyAnchorPointsModal from "../../Components/RoofSafetyAnchorPointsModal";
import { useFirebaseImage } from "../../Helpers/ImageLoader";
import { useBuilding } from "../../Helpers/BuildingContext";
import { ComponentFieldPatch } from "../../Helpers/componentPatch";
import { usePatchedComponent } from "../../hooks/usePatchedComponent";
import { useUserNames } from "../../Helpers/useUserNames";
import {
    getAnchorPointStatusColor,
    ACCESS_POINT_COLOR,
    getScaledPositionWithContain
} from "../../Helpers/RoofSafetyUtils";
import ComponentRelatedItems from "./ComponentRelatedItems";
import ComponentConnectedEvents from "./ComponentConnectedEvents";
import { belongsInRoom, resolveComponentLocation } from "../../Helpers/ComponentRoomUtils";
import { findBlueprintImage } from "../../Helpers/blueprintLookup";
import { Calendar, FileText, AlertTriangle, Edit3, Save, X, Upload, Trash2, Download, MapPin, ExternalLink } from "lucide-react";
import MiniMap from "../../Components/MiniMap";
import COLORS from "../../Helpers/colors";
import { Modal, Button } from "react-bootstrap";
import { componentWarrantyApi } from "../../api/componentWarrantyApi";
import { componentDocumentsApi } from "../../api/componentDocumentsApi";
import { ComponentDocument } from "../../types/protocols";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { componentFieldsApi } from "../../api/componentFieldsApi";
import { EnumPickerModal } from "../../Components/EnumPickerModal";
import { TextInputModal } from "../../Components/TextInputModal";
import { SpecificTypeModal, SpecificTypeSelection } from "../Förvaltning/MaintenanceDraft/components/modals/SpecificTypeModal";
import { UploadPickerModal } from "../../Components/UploadPickerModal";
import { getModularSize, formatModularSize } from "../../Helpers/modularSize";
import { buildComponentTypeDisplayName } from "../../Helpers/windowTypeName";

/** Fields applicable per BaseType, derived from iOS protocol definitions (Protocols.swift). */
const APPLICABLE_FIELDS: Partial<Record<BaseType, Set<string>>> = {
    [BaseType.Facade]: new Set(['material', 'color', 'area', 'thickness', 'insulation', 'cardinalDirection']),
    [BaseType.Balcony]: new Set(['material', 'color', 'area', 'linearMeters']),
    [BaseType.Roof]: new Set(['material', 'color', 'area', 'thickness', 'insulation', 'cardinalDirection', 'roofShape', 'roofSlope']),
    [BaseType.Foundation]: new Set(['material', 'area', 'insulation']),
    [BaseType.Chimney]: new Set(['material', 'color', 'width', 'height', 'depth']),
    [BaseType.RoofDrainage]: new Set(['material', 'color', 'linearMeters']),
    [BaseType.GroundDrainage]: new Set(['material', 'linearMeters']),
    [BaseType.Riser]: new Set(['material', 'linearMeters']),
    [BaseType.Outdoor]: new Set(['material', 'color', 'area', 'depth']),
    [BaseType.GeoTechnics]: new Set(['material', 'area']),
    [BaseType.Structural]: new Set(['material', 'color', 'area', 'depth', 'insulation']),
    [BaseType.Wall]: new Set(['area', 'thickness']),
    [BaseType.Floor]: new Set(['area', 'thickness']),
    [BaseType.Ceiling]: new Set(['area', 'thickness']),
    [BaseType.SheetMetal]: new Set(['color', 'area']),
    [BaseType.Utility]: new Set(['width', 'height', 'depth']),
    [BaseType.Miscellaneous]: new Set(['width', 'height', 'depth']),
    [BaseType.FixedFurnishing]: new Set(['width', 'height', 'depth']),
    [BaseType.Window]: new Set(['color', 'area']),
    [BaseType.Door]: new Set(['color', 'area']),
};
// Types not listed (Membrane) have no physical property fields from protocol.

/** Badge component for archived status - matches RiskBadge styling pattern */
const ArchivedBadge: React.FC = () => (
    <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: '#6c757d',
        color: 'white'
    }}>
        Arkiverad
    </span>
);

// Helper function to get type definition for a component
function getTypeDefinitionForComponent(
    component: BaseComponentImpl
): BuildComponentTypeDefinition | null {
    if (!component.specificType?.value) {
        return null;
    }

    return getAllBuildComponentTypes()[component.specificType.value] || null;
}

/** Display text for a ColorModel: translated enum, or "Färgkod X" for custom color codes. */
function formatColor(color: ColorModel | undefined): string {
    if (color?.colorEnum) return translate(color.colorEnum);
    if (color?.code) return `Färgkod ${color.code}`;
    return '–';
}

/** Interpolate color matching iOS ConditionPicker gradient */
function getDepreciationColor(percentage: number): string {
    const p = Math.max(0, Math.min(1, percentage));
    const stops = [
        { pos: 0, r: 168, g: 59, b: 96 },       // #a83b60 - dark rose
        { pos: 0.33, r: 254, g: 229, b: 173 },   // #fee5ad - beige
        { pos: 0.67, r: 254, g: 229, b: 173 },   // #fee5ad - beige
        { pos: 1.0, r: 103, g: 236, b: 170 },    // #67ecaa - green
    ];

    let lower = stops[0], upper = stops[stops.length - 1];
    for (let i = 0; i < stops.length - 1; i++) {
        if (p >= stops[i].pos && p <= stops[i + 1].pos) {
            lower = stops[i];
            upper = stops[i + 1];
            break;
        }
    }

    const range = upper.pos - lower.pos;
    const t = range === 0 ? 0 : (p - lower.pos) / range;
    const r = Math.round(lower.r + t * (upper.r - lower.r));
    const g = Math.round(lower.g + t * (upper.g - lower.g));
    const b = Math.round(lower.b + t * (upper.b - lower.b));

    return `rgb(${r}, ${g}, ${b})`;
}

// Small map preview component for RoofSafety section
interface RoofSafetyMapPreviewProps {
    building: Building;
    anchorPoints: { [key: string]: RoofAnchorPoint };
    accessPositions?: { [key: string]: RoofAccessPoint };
    anchorPointsUpdatedBy?: string | null;
    anchorPointsUpdatedWhen?: string | null;
    accessPositionsUpdatedBy?: string | null;
    accessPositionsUpdatedWhen?: string | null;
    userNames: { [uid: string]: string | null };
    onClick: () => void;
}

const RoofSafetyMapPreview: React.FC<RoofSafetyMapPreviewProps> = ({
    building,
    anchorPoints,
    accessPositions,
    anchorPointsUpdatedBy,
    anchorPointsUpdatedWhen,
    accessPositionsUpdatedBy,
    accessPositionsUpdatedWhen,
    userNames,
    onClick
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

    const currentMapImageKey = building.model?.currentMapImageKey;
    const mapImageRaw = currentMapImageKey && building.model?.mapImages?.[currentMapImageKey];
    const mapImage = mapImageRaw && typeof mapImageRaw !== 'string' ? mapImageRaw : null;
    const { src: mapSrc, isLoading } = useFirebaseImage(mapImage?.storagePath);

    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => resizeObserver.disconnect();
    }, []);

    const getScaledPosition = useCallback((position: BlueprintLocation) => {
        return getScaledPositionWithContain(position, containerDimensions, mapImage);
    }, [containerDimensions, mapImage]);

    const anchorPointsList = Object.entries(anchorPoints);
    const accessPositionsList = accessPositions ? Object.entries(accessPositions) : [];

    if (!mapImage) {
        // Still show metadata even without map
        if (anchorPointsUpdatedBy || accessPositionsUpdatedBy) {
            return (
                <div style={{
                    fontSize: '13px',
                    color: '#6c757d',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                }}>
                    {anchorPointsUpdatedBy && Object.keys(anchorPoints).length > 0 && (
                        <div>
                            <strong>Fästpunkter:</strong> Uppdaterad av {userNames[anchorPointsUpdatedBy] || 'Okänd användare'}
                            {anchorPointsUpdatedWhen && `, ${anchorPointsUpdatedWhen}`}
                        </div>
                    )}
                    {accessPositionsUpdatedBy && accessPositions && Object.keys(accessPositions).length > 0 && (
                        <div>
                            <strong>Accesspunkter:</strong> Uppdaterad av {userNames[accessPositionsUpdatedBy] || 'Okänd användare'}
                            {accessPositionsUpdatedWhen && `, ${accessPositionsUpdatedWhen}`}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    }

    return (
        <>
        <div
            ref={containerRef}
            onClick={onClick}
            style={{
                position: 'relative',
                width: '100%',
                height: '200px',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: '#f8f9fa',
                transition: 'box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
        >
            {isLoading ? (
                <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6c757d'
                }}>
                    Laddar...
                </div>
            ) : mapSrc ? (
                <>
                    <img
                        src={mapSrc}
                        alt="Byggnadskarta"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                        }}
                        onLoad={() => {
                            if (containerRef.current) {
                                setContainerDimensions({
                                    width: containerRef.current.offsetWidth,
                                    height: containerRef.current.offsetHeight
                                });
                            }
                        }}
                    />
                    {/* Anchor point markers */}
                    {anchorPointsList.map(([key, ap]) => {
                        if (!ap.position) return null;
                        const pos = getScaledPosition(ap.position);
                        if (!pos) return null;
                        return (
                            <div
                                key={key}
                                style={{
                                    position: 'absolute',
                                    left: `${pos.x}px`,
                                    top: `${pos.y}px`,
                                    transform: 'translate(-50%, -50%)',
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: getAnchorPointStatusColor(ap.safetyStatus),
                                    border: '2px solid white',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                                }}
                            />
                        );
                    })}
                    {/* Access position markers */}
                    {accessPositionsList.map(([key, accessPoint]) => {
                        const pos = getScaledPosition(accessPoint.position);
                        if (!pos) return null;
                        return (
                            <div
                                key={`access-${key}`}
                                style={{
                                    position: 'absolute',
                                    left: `${pos.x}px`,
                                    top: `${pos.y}px`,
                                    transform: 'translate(-50%, -50%)',
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: ACCESS_POINT_COLOR,
                                    border: '2px solid white',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                                }}
                            />
                        );
                    })}
                    {/* Overlay hint */}
                    <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                    }}>
                        Klicka för detaljer
                    </div>
                </>
            ) : (
                <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6c757d'
                }}>
                    Ingen karta tillgänglig
                </div>
            )}
        </div>
        {/* Update metadata */}
        {(anchorPointsUpdatedBy || accessPositionsUpdatedBy) && (
            <div style={{
                marginTop: '10px',
                fontSize: '13px',
                color: '#6c757d',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
            }}>
                {anchorPointsUpdatedBy && Object.keys(anchorPoints).length > 0 && (
                    <div>
                        <strong>Fästpunkter:</strong> Uppdaterad av {userNames[anchorPointsUpdatedBy] || 'Okänd användare'}
                        {anchorPointsUpdatedWhen && `, ${anchorPointsUpdatedWhen}`}
                    </div>
                )}
                {accessPositionsUpdatedBy && accessPositions && Object.keys(accessPositions).length > 0 && (
                    <div>
                        <strong>Accesspunkter:</strong> Uppdaterad av {userNames[accessPositionsUpdatedBy] || 'Okänd användare'}
                        {accessPositionsUpdatedWhen && `, ${accessPositionsUpdatedWhen}`}
                    </div>
                )}
            </div>
        )}
        </>
    );
};

interface ComponentDetailsViewProps {
    component: BaseComponentImpl;
    building: Building;
    buildingKey: string;
    showHeader?: boolean;
    headerActions?: React.ReactNode;
    containerStyle?: React.CSSProperties;
    containerClassName?: string;
    /** Vertical space between the sections (Allmänt, Egenskaper, Foton, ...). */
    sectionGap?: number;
    /** Space below each section heading. */
    headingGap?: number;
    onOverlayOpen?: () => void;
    onOverlayClose?: () => void;
    onImageClick?: (photo: PhotoModel, index: number, allPhotos: { [key: string]: PhotoModel }) => void;
    onChildComponentClick?: (childComponent: BaseComponentImpl) => void;
    /** Child shown in the preview pane, highlighted in the child photos. */
    selectedChildKey?: string | null;
    onDocumentClick?: (doc: ComponentDocument, pdfUrl: string) => void;
    onPhotoLocationClick?: (photoLocation: PhotoLocation) => void;
    onChildPhotoClick?: (photo: PhotoModel) => void;
    /** Key of the photo currently shown in the preview pane, to highlight its thumbnail. */
    selectedPhotoKey?: string;
    /**
     * Called after an edit is saved, with the fields written. The view shows its
     * own edits and patches the shared building (BuildingContext); a host that
     * keeps its own copy of the building applies the patch to it here.
     */
    onComponentPatched?: (componentKey: string, patch: ComponentFieldPatch) => void;
}

const ComponentDetailsView: React.FC<ComponentDetailsViewProps> = ({
    component: sourceComponent,
    building,
    buildingKey,
    showHeader = true,
    headerActions,
    containerStyle = {},
    containerClassName = 'std-box dropshadow',
    sectionGap = 30,
    headingGap = 15,
    onOverlayOpen,
    onOverlayClose,
    onImageClick,
    onChildComponentClick,
    selectedChildKey,
    onDocumentClick,
    onPhotoLocationClick,
    onChildPhotoClick,
    selectedPhotoKey,
    onComponentPatched,
}) => {
    const openPath = useOpenPath();
    // The host may keep showing the component it passed in; edits saved here are
    // laid over it until it catches up
    const { component, recordPatch } = usePatchedComponent(sourceComponent);
    const headingStyle: React.CSSProperties = { marginBottom: `${headingGap}px` };
    const { getTranslation, getLifetime } = useBuildComponentTypes();
    const { updateComponentFields } = useBuilding();

    // Every saved edit goes to this view, the shared building and the host's copy
    const commitPatch = useCallback((componentKey: string, patch: ComponentFieldPatch) => {
        recordPatch(componentKey, patch);
        updateComponentFields(componentKey, patch);
        onComponentPatched?.(componentKey, patch);
    }, [recordPatch, updateComponentFields, onComponentPatched]);

    const [showRoofSafetyModal, setShowRoofSafetyModal] = useState(false);
    const { userNames, loadUserNames } = useUserNames();
    const photoPins = useMemo(() => photoLocationList(component, building), [component, building]);

    // Get replacement component info
    const isArchived = !!component.archivedAt;
    const replacedByComponent = component.replacedByKey && building.model?.structure?.components
        ? building.model.structure.components[component.replacedByKey]
        : null;
    const replacedFromComponent = component.replacedFromKey && building.model?.structure?.components
        ? building.model.structure.components[component.replacedFromKey]
        : null;
    const replacedByName = replacedByComponent
        ? new BaseComponentImpl(replacedByComponent).displayName(building)
        : null;
    const replacedFromName = replacedFromComponent
        ? new BaseComponentImpl(replacedFromComponent).displayName(building)
        : null;

    // Warranty editing state
    const [editingWarrantyStartDate, setEditingWarrantyStartDate] = useState(false);
    const [editingWarrantyDuration, setEditingWarrantyDuration] = useState(false);
    const [warrantyStartDateValue, setWarrantyStartDateValue] = useState('');
    const [warrantyDurationValue, setWarrantyDurationValue] = useState('');
    const [savingWarranty, setSavingWarranty] = useState(false);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [isDocumentDragOver, setIsDocumentDragOver] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    const documents = component.documents;

    // Delete confirmation modal state
    const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<{ key: string; name: string; storageFilename: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Field editing state (admin-only)
    const isAdmin = useIsAdmin();
    const { types: buildComponentTypes } = useBuildComponentTypes();
    const { materials } = useMaterials();
    const specificTypeDefinition = component.specificType?.value ? buildComponentTypes[component.specificType.value] : undefined;

    type EditableField = 'specificType' | 'label' | 'cardinalDirection' | 'roofShape' | 'material' | 'category' | 'comment';
    const [editingField, setEditingField] = useState<EditableField | null>(null);

    // Modal state for text fields
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [showBrandModal, setShowBrandModal] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);

    // Load user names for RoofSafety and GPS metadata
    useEffect(() => {
        const uidsToLoad: string[] = [];
        if (component.safetyStatus?.updatedBy) {
            uidsToLoad.push(component.safetyStatus.updatedBy);
        }
        if (component.anchorPoints?.updatedBy) {
            uidsToLoad.push(component.anchorPoints.updatedBy);
        }
        if (component.accessPositions?.updatedBy) {
            uidsToLoad.push(component.accessPositions.updatedBy);
        }
        if (component.gpsCoordinates?.updatedBy) {
            uidsToLoad.push(component.gpsCoordinates.updatedBy);
        }
        if (uidsToLoad.length > 0) {
            loadUserNames(uidsToLoad);
        }
    }, [component.safetyStatus?.updatedBy, component.anchorPoints?.updatedBy, component.accessPositions?.updatedBy, component.gpsCoordinates?.updatedBy, loadUserNames]);

    // Warranty editing handlers
    const startEditingWarrantyStartDate = useCallback(() => {
        setWarrantyStartDateValue(component.warrantyStartDate?.value || '');
        setEditingWarrantyStartDate(true);
    }, [component.warrantyStartDate?.value]);

    const startEditingWarrantyDuration = useCallback(() => {
        setWarrantyDurationValue(component.warrantyDurationYears?.value?.toString() || '');
        setEditingWarrantyDuration(true);
    }, [component.warrantyDurationYears?.value]);

    const cancelEditingWarrantyStartDate = useCallback(() => {
        setEditingWarrantyStartDate(false);
        setWarrantyStartDateValue('');
    }, []);

    const cancelEditingWarrantyDuration = useCallback(() => {
        setEditingWarrantyDuration(false);
        setWarrantyDurationValue('');
    }, []);

    const saveWarrantyStartDate = useCallback(async () => {
        setSavingWarranty(true);
        try {
            const patch = await componentWarrantyApi.setWarrantyStartDate(
                buildingKey,
                component.key,
                warrantyStartDateValue || null
            );
            commitPatch(component.key, patch);
            setEditingWarrantyStartDate(false);
        } catch (error: any) {
            console.error('Error saving warranty start date:', error);
            alert(error.message || 'Kunde inte spara garantistart');
        } finally {
            setSavingWarranty(false);
        }
    }, [buildingKey, component.key, warrantyStartDateValue, commitPatch]);

    const saveWarrantyDuration = useCallback(async () => {
        const duration = warrantyDurationValue ? parseInt(warrantyDurationValue, 10) : null;

        setSavingWarranty(true);
        try {
            const patch = await componentWarrantyApi.setWarrantyDuration(
                buildingKey,
                component.key,
                duration
            );
            commitPatch(component.key, patch);
            setEditingWarrantyDuration(false);
        } catch (error: any) {
            console.error('Error saving warranty duration:', error);
            alert(error.message || 'Kunde inte spara garantitid');
        } finally {
            setSavingWarranty(false);
        }
    }, [buildingKey, component.key, warrantyDurationValue, commitPatch]);

    // Text field save handlers (for modal-based editing)
    const handleSaveLabel = useCallback(async (value: string) => {
        const patch = await componentFieldsApi.updateField(
            buildingKey,
            component.key,
            'label',
            value || null
        );
        commitPatch(component.key, patch);
    }, [buildingKey, component.key, commitPatch]);

    const handleSaveBrand = useCallback(async (value: string) => {
        const patch = await componentFieldsApi.updateField(
            buildingKey,
            component.key,
            'brand',
            value || null
        );
        commitPatch(component.key, patch);
    }, [buildingKey, component.key, commitPatch]);

    const handleSaveComment = useCallback(async (value: string) => {
        const patch = await componentFieldsApi.updateField(
            buildingKey,
            component.key,
            'comment',
            value || null
        );
        commitPatch(component.key, patch);
    }, [buildingKey, component.key, commitPatch]);

    const handleFieldSave = useCallback(async (field: Exclude<EditableField, 'specificType' | 'category'>, value: unknown) => {
        const patch = await componentFieldsApi.updateField(buildingKey, component.key, field, value);
        // Update local state immediately so the change is reflected without a reload
        commitPatch(component.key, patch);
        setEditingField(null);
    }, [buildingKey, component.key, commitPatch]);

    // Type and category are picked in the same modal and saved atomically,
    // so an incompatible category/type combination can't be written.
    const handleTypeSelectionSave = useCallback(async (selection: SpecificTypeSelection) => {
        const categoryField = getCategoryField(component.type);
        let patch;
        if (categoryField && selection.category) {
            patch = await componentFieldsApi.setTypeAndCategory(
                buildingKey,
                component.key,
                selection.typeKey,
                selection.category,
                categoryField,
                buildComponentTypes
            );
        } else {
            patch = await componentFieldsApi.updateField(buildingKey, component.key, 'specificType', selection.typeKey);
        }
        commitPatch(component.key, patch);
        setEditingField(null);
    }, [buildingKey, component.key, component.type, buildComponentTypes, commitPatch]);

    // Enum options for pickers
    const cardinalDirectionOptions = useMemo(() =>
        Object.values(CardinalDirection).map(d => ({ key: d, label: translate(d) })),
    []);

    const roofShapeOptions = useMemo(() =>
        Object.values(RoofShape).map(s => ({ key: s, label: translate(s) })),
    []);

    const materialOptions = useMemo(() =>
        Object.entries(materials).map(([key, def]) => ({ key, label: def.translations?.sv || key })),
    [materials]);

    const handleFiles = useCallback(async (files: File[]) => {
        const pdfFile = files.find(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
        if (!pdfFile) {
            alert('Endast PDF-filer är tillåtna');
            return;
        }

        setUploadingDocument(true);
        try {
            const newDoc = await componentDocumentsApi.uploadDocument(buildingKey, component.key, pdfFile);
            commitPatch(component.key, { documents: { ...documents, [newDoc.key]: newDoc } });
        } catch (error: any) {
            console.error('Error uploading document:', error);
            alert(error.message || 'Kunde inte ladda upp dokument');
        } finally {
            setUploadingDocument(false);
        }
    }, [buildingKey, component.key, documents, commitPatch]);

    const confirmDeleteDocument = useCallback(async () => {
        if (!deleteConfirmDoc) return;

        setIsDeleting(true);
        try {
            await componentDocumentsApi.deleteDocument(
                buildingKey,
                component.key,
                deleteConfirmDoc.key,
                deleteConfirmDoc.storageFilename
            );
            const { [deleteConfirmDoc.key]: _deleted, ...remaining } = documents ?? {};
            commitPatch(component.key, {
                documents: Object.keys(remaining).length > 0 ? remaining : null,
            });
            setDeleteConfirmDoc(null);
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Kunde inte ta bort dokument');
        } finally {
            setIsDeleting(false);
        }
    }, [buildingKey, component.key, deleteConfirmDoc, documents, commitPatch]);

    const handleDocumentClick = useCallback(async (doc: ComponentDocument) => {
        try {
            const url = await componentDocumentsApi.getDocumentUrl(buildingKey, component.key, doc.storageFilename);

            if (onDocumentClick) {
                // Use callback to show in preview pane
                onDocumentClick(doc, url);
            } else {
                // Open in new tab (default behavior for preview contexts)
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error('Error loading document:', error);
            alert('Kunde inte ladda dokument');
        }
    }, [buildingKey, component.key, onDocumentClick]);

    const handleDownloadDocument = useCallback(async (storageFilename: string, name: string) => {
        try {
            const url = await componentDocumentsApi.getDocumentUrl(buildingKey, component.key, storageFilename);

            // Force download
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = `${name}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error downloading document:', error);
            alert('Kunde inte ladda ner dokument');
        }
    }, [buildingKey, component.key]);

    // Drag and drop handlers for document upload
    const handleDocumentDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDocumentDragOver(true);
    }, []);

    const handleDocumentDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDocumentDragOver(false);
        }
    }, []);

    const handleDocumentDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDocumentDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    }, [handleFiles]);

    // NOTE: these lists are NOT rendered wholesale. The Allmänt panel does not
    // read metadataItems at all (its entries are dead), and Egenskaper renders `items` minus the labels it hand-renders as editable rows.
    // To show a new field, add a JSX row in the panel below; pushing here is not enough.
    const metadataItems = new Array<InfoItem>();
    const items = new Array<InfoItem>();
    const safetyItems = new Array<InfoItem>();
    const has = (field: string) => APPLICABLE_FIELDS[component.type]?.has(field) ?? false;

    // Get parent surface components
    const parentComponents: BaseComponentImpl[] = [];
    {
        const parentKeys = [
            component.parentSurface?.value,
            component.parentSurface1?.value,
            component.parentSurface2?.value,
        ].filter(Boolean) as string[];

        if (parentKeys.length > 0 && building.model?.structure?.components) {
            const seen = new Set<string>();
            parentKeys.forEach(key => {
                if (!seen.has(key) && building.model.structure?.components?.[key]) {
                    seen.add(key);
                    parentComponents.push(new BaseComponentImpl(building.model.structure.components[key]));
                }
            });
        }
    }

    // Get all components that have this component as parent, grouped by BaseType
    const childComponentsByType: Map<BaseType, BaseComponentImpl[]> = new Map();

    if (building.model?.structure?.components) {
        Object.values(building.model.structure.components).forEach(comp => {
            // Check all possible parent reference properties
            const parentRefs = [
                (comp as any).parentSurface?.value,
                (comp as any).parentSurface1?.value,
                (comp as any).parentSurface2?.value,
                (comp as any).parentKey?.value
            ].filter(Boolean);

            if (parentRefs.includes(component.key)) {
                const childComponent = new BaseComponentImpl(comp);
                const baseType = childComponent.type;

                if (!childComponentsByType.has(baseType)) {
                    childComponentsByType.set(baseType, []);
                }
                childComponentsByType.get(baseType)!.push(childComponent);
            }
        });
    }

    // Convert to sorted array by BaseType
    const sortedChildComponents = Array.from(childComponentsByType.entries())
        .sort(([typeA], [typeB]) => getTranslation(typeA).localeCompare(getTranslation(typeB), 'sv'));

    // For backward compatibility, keep windows reference
    const windows = childComponentsByType.get(BaseType.Window) || [];

    metadataItems.push({
        label: "Grundtyp",
        value: translate(component.type)
    });
    metadataItems.push({
        label: "Typ",
        value: specificTypeDefinition
            ? buildComponentTypeDisplayName(specificTypeDefinition, (materialKey) => materials[materialKey]?.translations?.sv)
                || getTranslation(component.specificType?.value ?? undefined, component.type)
            : component.specificType?.value ? getTranslation(component.specificType.value, component.type) : '–'
    });
    metadataItems.push({
        label: "Eget namn",
        value: component.label?.value || '–'
    });

    // Våning/Rum only for components placed so they belong in a room (not a facade, a roof-mounted fan …)
    const showRoomRows = belongsInRoom(component, building.model.structure);
    const location = resolveComponentLocation(component, building.model.structure);

    if (has('cardinalDirection')) {
        items.push({
            label: "Väderstreck",
            value: component.cardinalDirection?.value ? translate(component.cardinalDirection.value) : allDirectionsLabel(component.type)
        })
    }

    if (has('roofShape')) {
        items.push({
            label: "Takform",
            value: component.roofShape?.value ? translate(component.roofShape.value) : '–'
        });
    }
    if (has('roofSlope')) {
        items.push({
            label: "Taklutning",
            value: component.roofSlope?.value != null ? String(component.roofSlope.value) : '–'
        });
    }

    // Get type definition for merged properties
    const typeDefinition = getTypeDefinitionForComponent(component);

    // Material: instance value if present, otherwise the type definition's (matches iOS)
    const resolvedMaterial = component.material?.value ?? typeDefinition?.material?.value;
    if (has('material') || resolvedMaterial) {
        items.push({
            label: 'Material',
            value: resolvedMaterial ? resolvedMaterial.map((m: string) => getMaterialTranslation(m)) : '–'
        });
    }

    // Color: instance value if present, otherwise the type definition's (matches iOS)
    const resolvedColor = component.color?.value ?? typeDefinition?.color?.value;
    if (has('color') || resolvedColor) {
        items.push({ label: 'Färg', value: formatColor(resolvedColor) });
    }

    if (has('area')) {
        const area = component.areaShapes ? component.totalArea(building) : null;
        items.push({
            label: 'Area',
            value: area ? area.toFixed(1) + ' kvm' : '–'
        });

        if (component.areaShapes) {
            const areaExcl = component.totalAreaExcludingWindowsAndDoors(building);
            if (areaExcl !== null && area !== null && Math.abs(area - areaExcl) > 0.01) {
                items.push({
                    label: 'Area exkl fönster/dörrar',
                    value: areaExcl > 0 ? areaExcl.toFixed(1) + ' kvm' : '–'
                });
            }
        }
    }

    if (has('linearMeters')) {
        const linearMeters = component.linearMeters?.value;
        items.push({
            label: 'Löpmeter',
            value: linearMeters ? linearMeters.toFixed(1) + ' lm' : '–'
        });
    }

    if (has('height')) {
        const height = component.height?.value;
        items.push({
            label: 'Höjd',
            value: height ? (height * 1000) + ' mm' : '–'
        });
    }

    if (has('width')) {
        const width = component.width?.value;
        items.push({
            label: 'Bredd',
            value: width ? (width * 1000) + ' mm' : '–'
        });
    }

    if (has('depth')) {
        const depth = component.depth?.value;
        items.push({
            label: 'Djup',
            value: depth ? (depth * 1000) + ' mm' : '–'
        });
    }

    if (has('thickness')) {
        const thickness = component.thickness?.value;
        items.push({
            label: 'Tjocklek',
            value: thickness ? (thickness * 1000) + ' mm' : '–'
        });
    }

    if (has('insulation')) {
        const insulation = component.insulation?.value;
        items.push({
            label: 'Isolering',
            value: insulation ? (insulation * 1000) + ' mm' : '–'
        });
    }

    // Type-specific properties (window/door) — show applicable fields with dash when unset
    if (component.type === BaseType.Window) {
        items.push({
            label: 'Antal glasrutor',
            value: typeDefinition?.windowProps?.paneCount?.toString() ?? '–'
        });
        // U-värde is now stored on the instance
        items.push({
            label: 'U-värde',
            value: component.uvValue?.value?.toString() ?? '–'
        });
    }
    if (component.type === BaseType.Door) {
        items.push({
            label: 'Dörrkategori',
            value: typeDefinition?.doorProps?.doorCategory ? translate(typeDefinition.doorProps.doorCategory) : '–'
        });
    }
    if (component.type === BaseType.Window || component.type === BaseType.Door) {
        // Dimensions are stored in areaShapes as RectangleShape (in meters),
        // displayed in decimeters (e.g. "9x21" for 0.9m x 2.1m)
        const modularSize = getModularSize(component);
        items.push({
            label: 'Modulmått',
            value: modularSize ? formatModularSize(modularSize) : '–'
        });
    }

    // Collect hazards (instance-level only after migration)
    const allHazards = new Array<string>();

    if (component.hazardous?.value) {
        const hazardModel = component.hazardous.value;
        if (hazardModel.led) {
            allHazards.push(`${translate(HazardType.HazardLed)} (${translate(hazardModel.led)})`);
        }
        if (hazardModel.asbestos) {
            allHazards.push(`${translate(HazardType.HazardAsbestos)} (${translate(hazardModel.asbestos)})`);
        }
        if (hazardModel.arsenic) {
            allHazards.push(`${translate(HazardType.HazardArsenic)} (${translate(hazardModel.arsenic)})`);
        }
        if (hazardModel.blueConcrete) {
            allHazards.push(`${translate(HazardType.HazardBlueConcrete)} (${translate(hazardModel.blueConcrete)})`);
        }
        if (hazardModel.radon) {
            allHazards.push(`${translate(HazardType.HazardRadon)} (${translate(hazardModel.radon)})`);
        }
    }

    safetyItems.push({
        label: 'Miljö/hälsofara',
        value: allHazards.length > 0 ? allHazards : '–'
    });

    // Collect risks (instance-level only after migration)
    const allRisks = new Array<string>();

    if (component.buildingRisks?.value) {
        const riskConstruction = component.buildingRisks?.value;
        riskConstruction.risks?.map((risk) => {
            return allRisks.push(translate(risk));
        });
        if (riskConstruction?.customRisk) {
            allRisks.push(riskConstruction.customRisk);
        }
    }

    safetyItems.push({
        label: 'Riskkonstruktion',
        value: allRisks.length > 0 ? allRisks : '–'
    });

    // Child component counts
    sortedChildComponents.forEach(([baseType, components]) => {
        items.push({
            label: translate(baseType),
            value: `${components.length} st`
        });
    });

    // Condition & depreciation data (rendered as custom card in JSX)
    const conditionValue = component.condition?.value?.value ?? null;
    const conditionComment = component.condition?.value?.comment;
    const conditionDate = component.condition?.updatedWhen ? new Date(component.condition.updatedWhen) : null;

    const lifetime = component.specificType?.value ? getLifetime(component.specificType.value) : undefined;
    const directionalLifetime = lifetime ? getLifetimeForDirection(
        lifetime,
        component.cardinalDirection?.value || null
    ) : null;
    const expectedLifetimeYears = directionalLifetime?.expectedLifetimeYears ?? null;

    let yearsRemaining: number | null = null;
    let depreciationPercentage: number | null = null;
    let currentDegradedCondition: number | null = null;

    if (conditionValue !== null && expectedLifetimeYears) {
        const yearsFromCondition = (conditionValue / 10) * expectedLifetimeYears;
        let adjusted = yearsFromCondition;
        if (conditionDate) {
            const now = new Date();
            const yearsSinceAssessment = (now.getTime() - conditionDate.getTime()) / MS_PER_YEAR;
            adjusted = Math.max(0, yearsFromCondition - yearsSinceAssessment);

            // Calculate current degraded condition
            currentDegradedCondition = calculateCurrentCondition(
                conditionValue,
                conditionDate,
                expectedLifetimeYears
            );
        }
        yearsRemaining = Math.round(adjusted);
        depreciationPercentage = Math.max(0, Math.min(1, adjusted / expectedLifetimeYears));
    }

    // Warranty data calculations
    const warrantyStartDate = component.warrantyStartDate?.value;
    const warrantyDurationYears = component.warrantyDurationYears?.value;

    let warrantyExpiryDate: string | null = null;
    let warrantyExpired = false;
    let daysUntilExpiry: number | null = null;

    // Validate date format (YYYY-MM-DD) before parsing
    const isValidDateFormat = (dateStr: string): boolean => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
        const [year, month, day] = dateStr.split('-').map(Number);
        if (year < 1900 || year > 2100) return false;
        if (month < 1 || month > 12) return false;
        if (day < 1 || day > 31) return false;
        return true;
    };

    if (warrantyStartDate && warrantyDurationYears && isValidDateFormat(warrantyStartDate)) {
        const [year, month, day] = warrantyStartDate.split('-').map(Number);
        const startDate = new Date(year, month - 1, day);
        const expiryDate = new Date(startDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + warrantyDurationYears);

        warrantyExpiryDate = expiryDate.toISOString().substring(0, 10); // YYYY-MM-DD
        warrantyExpired = expiryDate < new Date();
        daysUntilExpiry = Math.ceil((expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    }

    const hasDocuments = Object.keys(documents ?? {}).length > 0;

    // Type comment (shown before component comment when exists)
    if (typeDefinition?.comment?.value) {
        items.push({
            label: 'Kommentar (typ)',
            value: typeDefinition.comment.value
        });
    }

    items.push({
        label: 'Kommentar',
        value: component.comment.value ?? "-",
    })

    const defaultContainerStyle = {
        padding: '30px',
        height: 'fit-content',
        ...containerStyle
    };

    return (
        <div className={containerClassName} style={defaultContainerStyle}>
            {showHeader && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                        <h3 style={{ margin: 0 }}>{component.displayName(building)}</h3>
                        {(allHazards.length > 0 || allRisks.length > 0 || isArchived) && (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                                {isArchived && <ArchivedBadge />}
                                {allHazards.length > 0 && <RiskBadge type="hazard" />}
                                {allRisks.length > 0 && <RiskBadge type="riskConstruction" />}
                            </div>
                        )}
                    </div>
                    {headerActions}
                </div>
            )}

            {/* Archived badge and replacement links - always shown */}
            {(isArchived || replacedByName || replacedFromName) && (
                <div style={{ marginBottom: '20px' }}>
                    {/* Show archived badge when header is hidden */}
                    {!showHeader && isArchived && (
                        <div style={{ marginBottom: '8px' }}>
                            <ArchivedBadge />
                        </div>
                    )}
                    {/* Replacement links */}
                    {(replacedByName || replacedFromName) && (
                        <div style={{ fontSize: '14px', color: '#6c757d' }}>
                            {replacedByName && (
                                <div style={{ marginBottom: '4px' }}>
                                    Ersatt av:{' '}
                                    <Link
                                        to={`/building/${buildingKey}/component/${component.replacedByKey}`}
                                    >
                                        {replacedByName}
                                    </Link>
                                </div>
                            )}
                            {replacedFromName && (
                                <div>
                                    Ersatte:{' '}
                                    <Link
                                        to={`/building/${buildingKey}/component/${component.replacedFromKey}`}
                                    >
                                        {replacedFromName}
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: `${sectionGap}px` }}>
                <div>
                    <h4 style={headingStyle}>Allmänt</h4>
                    {/* Rows here are hand-written JSX (metadataItems is not rendered). Add new rows here. */}
                    <div className="info-panel">
                        {/* Grundtyp - read-only */}
                        <div className="info-row">
                            <div className="info-label">Grundtyp</div>
                            <div className="info-value">{translate(component.type)}</div>
                        </div>

                        {/* Kategori - editable by admin (Utility/Outdoor only) */}
                        {(component.type === BaseType.Utility || component.type === BaseType.Outdoor) && (
                            <div className="info-row">
                                <div className="info-label">Kategori</div>
                                <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>
                                        {component.type === BaseType.Utility
                                            ? (component.utilityCategory ? translate(component.utilityCategory) : '–')
                                            : (component.outdoorCategory ? translate(component.outdoorCategory) : '–')}
                                    </span>
                                    {isAdmin && (
                                        <button
                                            className="edit-field-icon"
                                            onClick={() => setEditingField('category')}
                                            title="Redigera kategori"
                                        >
                                            <Edit3 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Typ - editable by admin */}
                        <div className="info-row">
                            <div className="info-label">Typ</div>
                            <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>{component.specificType?.value ? getTranslation(component.specificType.value, component.type) : '–'}</span>
                                {isAdmin && (
                                    <button
                                        className="edit-field-icon"
                                        onClick={() => setEditingField('specificType')}
                                        title="Redigera typ"
                                    >
                                        <Edit3 size={14} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Eget namn - editable by admin (inline) */}
                        <div className="info-row">
                            <div className="info-label">Eget namn</div>
                            <div className="info-value">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{component.label?.value || '–'}</span>
                                    {isAdmin && (
                                        <button
                                            className="edit-field-icon"
                                            onClick={() => setShowLabelModal(true)}
                                            title="Redigera eget namn"
                                        >
                                            <Edit3 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Märke - editable by admin, utilities and fast inredning only (Övrigt's brand is an iOS inheritance artefact) */}
                        {(component.type === BaseType.Utility || component.type === BaseType.FixedFurnishing) && (
                            <div className="info-row">
                                <div className="info-label">Märke</div>
                                <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{component.brand?.value || '–'}</span>
                                    {isAdmin && (
                                        <button
                                            className="edit-field-icon"
                                            onClick={() => setShowBrandModal(true)}
                                            title="Redigera märke"
                                        >
                                            <Edit3 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Våning and Rum - read-only */}
                        {showRoomRows && (
                            <>
                                <div className="info-row">
                                    <div className="info-label">Våning</div>
                                    <div className="info-value">{location.levelDisplay || '–'}</div>
                                </div>
                                <div className="info-row">
                                    <div className="info-label">Rum</div>
                                    <div className="info-value">{location.roomDisplay || '–'}</div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {(allHazards.length > 0 || allRisks.length > 0) && (
                    <div>
                        <h4 style={headingStyle}>Miljö & Risk</h4>
                        <InfoPanel items={safetyItems} />
                    </div>
                )}
                <div>
                    <h4 style={headingStyle}>Egenskaper</h4>
                    <div className="info-panel">
                        {/* Väderstreck - editable by admin */}
                        {has('cardinalDirection') && (
                            <div className="info-row">
                                <div className="info-label">Väderstreck</div>
                                <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{component.cardinalDirection?.value ? translate(component.cardinalDirection.value) : allDirectionsLabel(component.type)}</span>
                                    {isAdmin && (
                                        <button
                                            className="edit-field-icon"
                                            onClick={() => setEditingField('cardinalDirection')}
                                            title="Redigera väderstreck"
                                        >
                                            <Edit3 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Takform - editable by admin (Roof only) */}
                        {has('roofShape') && (
                            <div className="info-row">
                                <div className="info-label">Takform</div>
                                <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{component.roofShape?.value ? translate(component.roofShape.value) : '–'}</span>
                                    {isAdmin && (
                                        <button
                                            className="edit-field-icon"
                                            onClick={() => setEditingField('roofShape')}
                                            title="Redigera takform"
                                        >
                                            <Edit3 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Material - editable by admin */}
                        {(has('material') || component.material?.value) && (
                            <div className="info-row">
                                <div className="info-label">Material</div>
                                <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>
                                        {component.material?.value && component.material.value.length > 0
                                            ? component.material.value.map(m => getMaterialTranslation(m)).join(', ')
                                            : '–'}
                                    </span>
                                    {isAdmin && (
                                        <button
                                            className="edit-field-icon"
                                            onClick={() => setEditingField('material')}
                                            title="Redigera material"
                                        >
                                            <Edit3 size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Remaining non-editable properties */}
                        {items
                            .filter(item => !['Väderstreck', 'Takform', 'Material'].includes(item.label))
                            .map((item, index) => {
                                const valueArray = Array.isArray(item.value) ? item.value : null;
                                const isCommentField = item.label === 'Kommentar';
                                return (
                                    <div key={`${item.label}-${index}`} className="info-row">
                                        <div className="info-label">{item.label}</div>
                                        <div className="info-value" style={{
                                            color: item.valueColor ?? '#000000',
                                            display: isCommentField && isAdmin ? 'flex' : undefined,
                                            alignItems: isCommentField && isAdmin ? 'flex-start' : undefined,
                                            gap: isCommentField && isAdmin ? '8px' : undefined
                                        }}>
                                            <span style={{ whiteSpace: isCommentField ? 'pre-wrap' : undefined }}>
                                                {valueArray
                                                    ? valueArray.map((v, i) => (
                                                        <React.Fragment key={i}>
                                                            {v}
                                                            {i < valueArray.length - 1 && <br />}
                                                        </React.Fragment>
                                                    ))
                                                    : item.value}
                                            </span>
                                            {isCommentField && isAdmin && (
                                                <button
                                                    className="edit-field-icon"
                                                    onClick={() => setShowCommentModal(true)}
                                                    title="Redigera kommentar"
                                                    style={{ flexShrink: 0 }}
                                                >
                                                    <Edit3 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* Condition & Depreciation Card - high value, shown right after Allmänt & Egenskaper */}
                <div>
                    <h4 style={headingStyle}>Skick & Avskrivning</h4>
                    {conditionValue !== null ? (
                        <div style={{
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '16px',
                            border: '1px solid #e9ecef'
                        }}>
                            {/* Condition display - shows degraded value when applicable */}
                            {(() => {
                                const hasDegraded = currentDegradedCondition !== null && currentDegradedCondition < conditionValue;
                                const displayCondition = hasDegraded ? currentDegradedCondition : conditionValue;
                                const degradationAmount = currentDegradedCondition !== null ? conditionValue - currentDegradedCondition : 0;

                                return (
                                    <div style={{ marginBottom: expectedLifetimeYears ? '12px' : '0' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'baseline',
                                            marginBottom: hasDegraded ? '4px' : '0'
                                        }}>
                                            <span style={{ fontWeight: 600, fontSize: '18px' }}>
                                                {displayCondition!.toFixed(1)}/10
                                            </span>
                                            <span style={{ color: '#6c757d', fontSize: '13px' }}>
                                                {hasDegraded ? 'Beräknat nuvarande skick' : (
                                                    conditionDate ? `Bedömt ${conditionDate.toLocaleDateString('sv-SE')}` : null
                                                )}
                                            </span>
                                        </div>
                                        {hasDegraded && (
                                            <div style={{ fontSize: '13px', color: '#6c757d', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                <span>
                                                    Bedömt {conditionValue.toFixed(1)}/10
                                                    {conditionDate && ` (${conditionDate.toLocaleDateString('sv-SE')})`}
                                                </span>
                                                <span style={{ color: '#ffc107' }}>
                                                    −{degradationAmount.toFixed(1)} sedan bedömning
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {expectedLifetimeYears && depreciationPercentage !== null && yearsRemaining !== null && (
                                <>
                                    <div style={{
                                        height: '10px',
                                        background: '#e9ecef',
                                        borderRadius: '5px',
                                        overflow: 'hidden',
                                        marginBottom: '8px'
                                    }}>
                                        <div style={{
                                            width: `${depreciationPercentage * 100}%`,
                                            height: '100%',
                                            background: getDepreciationColor(depreciationPercentage),
                                            borderRadius: '5px',
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                        {yearsRemaining < 1 ? 'Mindre än 1 år kvar' : `${yearsRemaining} år kvar`} av {expectedLifetimeYears} år
                                    </div>
                                </>
                            )}

                            {conditionComment && (
                                <div style={{
                                    marginTop: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid #e9ecef',
                                    fontSize: '14px',
                                    color: '#495057',
                                    fontStyle: 'italic'
                                }}>
                                    {conditionComment}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ color: '#6c757d', fontSize: '14px' }}>Ej bedömt</div>
                    )}
                </div>

                {/* RoofSafety Section - custom base-type data, shown early (before Foton) */}
                {component.type === BaseType.RoofSafety && (
                    <div>
                        <h4 style={headingStyle}>Taksäkerhet</h4>
                        {/* Safety Status & Access Info */}
                        {(component.safetyStatus?.value || component.accessInfo?.value) && (
                            <div style={{ marginBottom: '15px' }}>
                                <InfoPanel className="info-panel--stacked" items={[
                                    ...(component.safetyStatus?.value ? [{
                                        label: 'Säkerhetsstatus',
                                        value: translate(component.safetyStatus.value) + (
                                            (component.safetyStatus.updatedBy || component.safetyStatus.updatedWhen)
                                                ? `\nSatt ${component.safetyStatus.updatedBy ? `av ${userNames[component.safetyStatus.updatedBy] || 'Okänd användare'}` : ''}${component.safetyStatus.updatedBy && component.safetyStatus.updatedWhen ? ', ' : ''}${component.safetyStatus.updatedWhen || ''}`
                                                : ''
                                        )
                                    }] : []),
                                    ...(component.accessInfo?.value ? [{ label: 'Takaccess-information', value: component.accessInfo.value }] : [])
                                ]} />
                            </div>
                        )}
                        {/* Anchor Points and Access Positions - Map Preview */}
                        {((component.anchorPoints?.value && Object.keys(component.anchorPoints.value).length > 0) ||
                          (component.accessPositions?.value && Object.keys(component.accessPositions.value).length > 0)) && (
                            <div style={{ marginBottom: '15px' }}>
                                <h5 style={{ marginBottom: '10px', fontWeight: 600 }}>Karta</h5>
                                <RoofSafetyMapPreview
                                    building={building}
                                    anchorPoints={component.anchorPoints?.value || {}}
                                    accessPositions={component.accessPositions?.value || undefined}
                                    anchorPointsUpdatedBy={component.anchorPoints?.updatedBy}
                                    anchorPointsUpdatedWhen={component.anchorPoints?.updatedWhen}
                                    accessPositionsUpdatedBy={component.accessPositions?.updatedBy}
                                    accessPositionsUpdatedWhen={component.accessPositions?.updatedWhen}
                                    userNames={userNames}
                                    onClick={() => setShowRoofSafetyModal(true)}
                                />
                                <RoofSafetyAnchorPointsModal
                                    show={showRoofSafetyModal}
                                    onHide={() => setShowRoofSafetyModal(false)}
                                    building={building}
                                    buildingKey={buildingKey}
                                    anchorPoints={component.anchorPoints?.value || {}}
                                    accessPositions={component.accessPositions?.value || undefined}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Foton - high value, shown right below info panels */}
                {component.photos && (
                    <div>
                        <h4 style={headingStyle}>Foton</h4>
                        <PhotoGallery
                            photos={component.photos}
                            buildingKey={buildingKey}
                            onFancyboxOpen={onOverlayOpen}
                            onFancyboxClose={onOverlayClose}
                            selectedPhotoKey={selectedPhotoKey}
                            onImageClick={onImageClick
                                ? (photo, _url, index) => onImageClick(photo, index, component.photos!)
                                : undefined}
                        />
                    </div>
                )}

                {/* Photo locations: one per pinned parent-surface photo, primary pin first */}
                {photoPins.length > 0 && (
                    <div>
                        {onPhotoLocationClick ? (
                            <>
                                <h4 style={headingStyle}>Position i foto</h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                    {photoPins.map(({ photoKey, location, side }) => (
                                        <div key={photoKey}>
                                            <PhotoThumbnailWithMarker
                                                photoLocation={location}
                                                size={150}
                                                onClick={() => onPhotoLocationClick(location)}
                                            />
                                            <div style={{ fontSize: '8pt', color: '#666', marginTop: '4px' }}>
                                                {PHOTO_LOCATION_SIDE_LABELS[side]}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            photoPins.map(({ photoKey, location, side }) => (
                                <PhotoWithMarker
                                    key={photoKey}
                                    photoLocation={location}
                                    title={photoLocationLabel(side)}
                                    onOverlayOpen={onOverlayOpen}
                                    onOverlayClose={onOverlayClose}
                                />
                            ))
                        )}
                    </div>
                )}

                {/* Positions (Map and Blueprint Locations) */}
                {((component.mapLocations && Object.keys(component.mapLocations).length > 0) ||
                    (component.blueprintLocations && Object.keys(component.blueprintLocations).length > 0)) && (
                        <div>
                            <div>
                                {/* Map Locations */}
                                {component.mapLocations && Object.keys(component.mapLocations).length > 0 && building?.model?.mapImages && Object.keys(building.model.mapImages).length > 0 &&
                                    Object.entries(component.mapLocations).map(([blueprintKey, location]) => {
                                        // Only show if this is the current map image key
                                        if (blueprintKey !== building.model.currentMapImageKey) return null;

                                        const mapImage = building.model.mapImages?.[blueprintKey];

                                        if (!mapImage) return null;

                                        return (
                                            <div key={`map-${blueprintKey}`} style={{ marginBottom: '15px' }}>
                                                <BlueprintWithMarkers
                                                    blueprint={mapImage}
                                                    currentBlueprintKey={blueprintKey}
                                                    building={building}
                                                    pointMarker={location}
                                                    pointMarkerLabel={component.displayName(building)}
                                                    title="Position på satellitbild"
                                                />
                                            </div>
                                        );
                                    })
                                }

                                {/* Blueprint Locations */}
                                {component.blueprintLocations && Object.keys(component.blueprintLocations).length > 0 &&
                                    Object.entries(component.blueprintLocations).map(([blueprintKey, location]) => {
                                        // A level's drawing or the roof drawing; only the owner's current drawing is shown
                                        const found = findBlueprintImage(building?.model?.structure, blueprintKey);
                                        if (!found?.isCurrent) return null;
                                        const title = found.owner.kind === 'roofPlan' ? 'Position på takplan' : `Position på ${found.label}`;

                                        return (
                                            <div key={`blueprint-${blueprintKey}`} style={{ marginBottom: '15px' }}>
                                                <BlueprintWithMarkers
                                                    blueprint={found.image}
                                                    currentBlueprintKey={blueprintKey}
                                                    building={building}
                                                    pointMarker={location}
                                                    pointMarkerLabel={component.displayName(building)}
                                                    title={title}
                                                />
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    )}

                {/* GPS Coordinates Section (read-only) */}
                {component.gpsCoordinates?.value && (
                    <div>
                        <h4 style={headingStyle}>
                            <MapPin size={16} style={{ marginRight: '8px', verticalAlign: 'text-bottom' }} />
                            GPS-position
                        </h4>
                        <div style={{
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '16px',
                            border: '1px solid #e9ecef'
                        }}>
                            {/* Non-interactive mini-map preview */}
                            <div style={{ marginBottom: '12px', pointerEvents: 'none' }}>
                                <MiniMap
                                    coordinates={component.gpsCoordinates.value}
                                    height={180}
                                />
                            </div>

                            {/* Metadata: who/when updated */}
                            {(component.gpsCoordinates.updatedBy || component.gpsCoordinates.updatedWhen) && (
                                <div style={{
                                    fontSize: '13px',
                                    color: '#6c757d',
                                    marginBottom: '12px'
                                }}>
                                    Uppdaterad
                                    {component.gpsCoordinates.updatedBy && (
                                        <> av {userNames[component.gpsCoordinates.updatedBy] || 'Okänd användare'}</>
                                    )}
                                    {component.gpsCoordinates.updatedWhen && (
                                        <>, {component.gpsCoordinates.updatedWhen.substring(0, 10)}</>
                                    )}
                                </div>
                            )}

                            {/* Open in Google Maps link */}
                            <a
                                href={`https://www.google.com/maps?q=${component.gpsCoordinates.value.latitude},${component.gpsCoordinates.value.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: '#0d6efd',
                                    textDecoration: 'none',
                                    fontSize: '14px'
                                }}
                            >
                                <ExternalLink size={14} />
                                Öppna i Google Maps
                            </a>
                        </div>
                    </div>
                )}

                {/* Documents Section */}
                <div
                    onDragOver={handleDocumentDragOver}
                    onDragLeave={handleDocumentDragLeave}
                    onDrop={handleDocumentDrop}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...headingStyle }}>
                        <h4 style={{ margin: 0 }}>Dokument</h4>
                        <button
                            onClick={() => setShowUploadModal(true)}
                            disabled={uploadingDocument}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                border: '1px solid #dee2e6',
                                background: 'white',
                                cursor: uploadingDocument ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px'
                            }}
                        >
                            <Upload size={14} />
                            {uploadingDocument ? 'Laddar upp...' : 'Ladda upp PDF'}
                        </button>
                    </div>

                    {hasDocuments && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                            {Object.entries(documents ?? {}).map(([key, doc]) => (
                                <div
                                    key={key}
                                    onClick={() => handleDocumentClick(doc as ComponentDocument)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 12px',
                                        background: '#f8f9fa',
                                        borderRadius: '4px',
                                        border: '1px solid #e9ecef',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                >
                                    <FileText size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {doc.name}
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadDocument(doc.storageFilename, doc.name);
                                        }}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: '1px solid #dee2e6',
                                            background: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        title="Ladda ner"
                                    >
                                        <Download size={14} />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteConfirmDoc({ key, name: doc.name, storageFilename: doc.storageFilename });
                                        }}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: '1px solid #dc3545',
                                            background: 'white',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        title="Ta bort"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Drop zone: the whole section accepts drops, the dashed strip shows where */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '16px',
                            borderRadius: '8px',
                            border: `2px dashed ${isDocumentDragOver ? COLORS.primary : '#dee2e6'}`,
                            color: isDocumentDragOver ? COLORS.primary : '#6c757d',
                            fontSize: '14px',
                            textAlign: 'center',
                            transition: 'border-color 0.2s ease, color 0.2s ease'
                        }}
                    >
                        <Upload size={16} style={{ flexShrink: 0 }} />
                        Dra och släpp PDF-filer här
                    </div>
                </div>

                {sortedChildComponents.map(([baseType, components]) => (
                    <ChildComponentsSection
                        key={baseType}
                        parent={component}
                        baseType={baseType}
                        components={components}
                        building={building}
                        buildingKey={buildingKey}
                        headingStyle={headingStyle}
                        selectedChildKey={selectedChildKey}
                        onChildClick={onChildComponentClick
                            ?? ((childComponent, event) => openPath(`/building/${buildingKey}/component/${childComponent.key}`, event))}
                        onChildPhotoClick={onChildPhotoClick}
                        onPhotoLocationClick={onPhotoLocationClick}
                    />
                ))}

                {parentComponents.length > 0 && (
                    <div>
                        <h4 style={headingStyle}>Tillhör</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {parentComponents.map((parentComp) => (
                                <ParentComponentCard
                                    key={parentComp.key}
                                    component={parentComp}
                                    building={building}
                                    buildingKey={buildingKey}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Warranty Section */}
                <div>
                    <h4 style={headingStyle}>Garanti</h4>
                    <div style={{
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid #e9ecef'
                    }}>
                        {/* Warranty start date - always editable */}
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                <Calendar size={14} style={{ color: '#6c757d' }} />
                                <span style={{ fontSize: '13px', color: '#6c757d' }}>Garantistart</span>
                            </div>
                            {editingWarrantyStartDate ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="date"
                                        value={warrantyStartDateValue}
                                        onChange={(e) => setWarrantyStartDateValue(e.target.value)}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            border: '1px solid #ced4da',
                                            fontSize: '14px'
                                        }}
                                        disabled={savingWarranty}
                                    />
                                    <button
                                        onClick={saveWarrantyStartDate}
                                        disabled={savingWarranty}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            border: 'none',
                                            background: '#28a745',
                                            color: 'white',
                                            cursor: savingWarranty ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Save size={14} />
                                    </button>
                                    <button
                                        onClick={cancelEditingWarrantyStartDate}
                                        disabled={savingWarranty}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            border: '1px solid #6c757d',
                                            background: 'white',
                                            cursor: savingWarranty ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontWeight: warrantyStartDate ? 600 : 400, color: warrantyStartDate ? 'inherit' : '#6c757d' }}>
                                        {warrantyStartDate || 'Ej angivet'}
                                    </span>
                                    <button
                                        onClick={startEditingWarrantyStartDate}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: '1px solid #dee2e6',
                                            background: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Edit3 size={14} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Warranty duration - always editable */}
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>
                                Garantitid (år)
                            </div>
                            {editingWarrantyDuration ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="number"
                                        min="1"
                                        max="99"
                                        value={warrantyDurationValue}
                                        onChange={(e) => setWarrantyDurationValue(e.target.value)}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            border: '1px solid #ced4da',
                                            fontSize: '14px',
                                            width: '80px'
                                        }}
                                        disabled={savingWarranty}
                                    />
                                    <button
                                        onClick={saveWarrantyDuration}
                                        disabled={savingWarranty}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            border: 'none',
                                            background: '#28a745',
                                            color: 'white',
                                            cursor: savingWarranty ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Save size={14} />
                                    </button>
                                    <button
                                        onClick={cancelEditingWarrantyDuration}
                                        disabled={savingWarranty}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '4px',
                                            border: '1px solid #6c757d',
                                            background: 'white',
                                            cursor: savingWarranty ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontWeight: warrantyDurationYears ? 600 : 400, color: warrantyDurationYears ? 'inherit' : '#6c757d' }}>
                                        {warrantyDurationYears ? `${warrantyDurationYears} år` : 'Ej angivet'}
                                    </span>
                                    <button
                                        onClick={startEditingWarrantyDuration}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: '1px solid #dee2e6',
                                            background: 'white',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Edit3 size={14} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Calculated expiry date */}
                        {warrantyExpiryDate && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                    {warrantyExpired && <AlertTriangle size={14} style={{ color: '#dc3545' }} />}
                                    <span style={{ fontSize: '13px', color: warrantyExpired ? '#dc3545' : '#6c757d' }}>
                                        Garanti utgår
                                    </span>
                                </div>
                                <span style={{
                                    fontWeight: 600,
                                    color: warrantyExpired ? '#dc3545' : 'inherit'
                                }}>
                                    {warrantyExpiryDate}
                                    {warrantyExpired && (
                                        <span style={{
                                            marginLeft: '8px',
                                            padding: '2px 8px',
                                            background: '#dc3545',
                                            color: 'white',
                                            borderRadius: '4px',
                                            fontSize: '12px'
                                        }}>
                                            Utgången
                                        </span>
                                    )}
                                    {!warrantyExpired && daysUntilExpiry !== null && daysUntilExpiry <= 90 && (
                                        <span style={{
                                            marginLeft: '8px',
                                            padding: '2px 8px',
                                            background: '#ffc107',
                                            color: '#212529',
                                            borderRadius: '4px',
                                            fontSize: '12px'
                                        }}>
                                            {daysUntilExpiry} dagar kvar
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}

                        {/* Partial data hints */}
                        {warrantyStartDate && !warrantyDurationYears && (
                            <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                Ange garantitid för att se utgångsdatum
                            </div>
                        )}
                        {!warrantyStartDate && warrantyDurationYears && (
                            <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                Ange garantistart för att se utgångsdatum
                            </div>
                        )}
                    </div>
                </div>

                {/* Connected timeline events (custom events + component replacements) */}
                <ComponentConnectedEvents
                    building={building}
                    componentKey={component.key}
                />

                {/* Related activities section */}
                <ComponentRelatedItems
                    buildingKey={buildingKey}
                    componentKey={component.key}
                    buildingName={building.summary?.name}
                />
            </div>

            {/* Delete document confirmation modal */}
            <Modal show={!!deleteConfirmDoc} onHide={() => setDeleteConfirmDoc(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ta bort dokument</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Är du säker på att du vill ta bort dokumentet <strong>{deleteConfirmDoc?.name}</strong>?
                    </p>
                    <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: 0 }}>
                        Detta går inte att ångra.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteConfirmDoc(null)} disabled={isDeleting}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={confirmDeleteDocument} disabled={isDeleting}>
                        {isDeleting ? 'Tar bort...' : 'Ta bort'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Specific Type Modal — for Utility/Outdoor the category is picked
                in the same modal and saved together with the type */}
            {(editingField === 'specificType' || editingField === 'category') && (
                <SpecificTypeModal
                    component={component}
                    building={building}
                    initialView={editingField === 'category' ? 'category' : 'type'}
                    onSave={handleTypeSelectionSave}
                    onClose={() => setEditingField(null)}
                />
            )}

            {/* Cardinal Direction Picker Modal */}
            <EnumPickerModal
                show={editingField === 'cardinalDirection'}
                onHide={() => setEditingField(null)}
                onSave={async (value) => {
                    await handleFieldSave('cardinalDirection', value as string);
                }}
                title="Välj väderstreck"
                options={cardinalDirectionOptions}
                currentValue={component.cardinalDirection?.value || null}
            />

            {/* Roof Shape Picker Modal */}
            <EnumPickerModal
                show={editingField === 'roofShape'}
                onHide={() => setEditingField(null)}
                onSave={async (value) => {
                    await handleFieldSave('roofShape', value as string);
                }}
                title="Välj takform"
                options={roofShapeOptions}
                currentValue={component.roofShape?.value || null}
            />

            {/* Material Picker Modal (multi-select) */}
            <EnumPickerModal
                show={editingField === 'material'}
                onHide={() => setEditingField(null)}
                onSave={async (value) => {
                    await handleFieldSave('material', value as string[]);
                }}
                title="Välj material"
                options={materialOptions}
                currentValue={component.material?.value || []}
                multiSelect={true}
            />

            {/* Label Text Input Modal */}
            <TextInputModal
                show={showLabelModal}
                onHide={() => setShowLabelModal(false)}
                onSave={handleSaveLabel}
                title="Redigera eget namn"
                currentValue={component.label?.value || ''}
                placeholder="Ange eget namn"
            />

            {/* Brand Text Input Modal */}
            <TextInputModal
                show={showBrandModal}
                onHide={() => setShowBrandModal(false)}
                onSave={handleSaveBrand}
                title="Redigera märke"
                currentValue={component.brand?.value || ''}
                placeholder="t.ex. Nibe, Gustavsberg"
            />

            {/* Comment Text Input Modal */}
            <TextInputModal
                show={showCommentModal}
                onHide={() => setShowCommentModal(false)}
                onSave={handleSaveComment}
                title="Redigera kommentar"
                currentValue={component.comment?.value || ''}
                placeholder="Ange kommentar"
                multiline={true}
            />

            {/* Upload Picker Modal */}
            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                accept=".pdf,application/pdf"
                multiple={false}
                title="Ladda upp"
            />
        </div>
    );
};

export default ComponentDetailsView;
